/**
 * Color mode for the Chakra system, backed by `next-themes` (Chakra v3's documented
 * strategy — works in plain Vite/React, not just Next). next-themes writes
 * `class="light"|"dark"` on <html>, which drives Chakra's `_dark` / `_light`
 * conditions. Default is light; dark is available via `toggleColorMode`.
 */
import { ThemeProvider, useTheme } from 'next-themes';
import type { ReactNode } from 'react';

export type ColorMode = 'light' | 'dark';

export function ColorModeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      disableTransitionOnChange
    >
      {children}
    </ThemeProvider>
  );
}

export function useColorMode() {
  const { resolvedTheme, setTheme, forcedTheme } = useTheme();
  const colorMode = (forcedTheme ?? resolvedTheme ?? 'light') as ColorMode;
  return {
    colorMode,
    setColorMode: (mode: ColorMode) => setTheme(mode),
    toggleColorMode: () => setTheme(colorMode === 'dark' ? 'light' : 'dark'),
  };
}

/** Pick a value based on the active color mode (light-first). */
export function useColorModeValue<T>(light: T, dark: T): T {
  return useColorMode().colorMode === 'dark' ? dark : light;
}
